import React, { useEffect, useRef, useState } from 'react';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import { FocusTrap, FocusTrapOptions } from './focusTrap';

interface AccessibleDialogProps extends Omit<DialogProps, 'onEnter' | 'onExited' | 'onClose'> {
    /** Element to return focus to when dialog closes */
    returnFocusElement?: HTMLElement;
    /** Callback when dialog opens */
    onEnter?: () => void;
    /** Callback when dialog closes */
    onExited?: () => void;
    /** Callback when dialog should close */
    onClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
    /** Initial element to focus when dialog opens */
    initialFocus?: HTMLElement;
    /** Whether to enforce reading order within the dialog */
    enforceReadingOrder?: boolean;
    /** Custom reading order elements (overrides automatic detection) */
    readingOrderElements?: HTMLElement[];
}

/**
 * Accessible Dialog Component with Focus Trapping
 * 
 * This component wraps Material-UI Dialog and adds:
 * - Proper focus trapping within the dialog
 * - Focus management when opening/closing
 * - Keyboard navigation (Tab/Shift+Tab cycling, Arrow keys)
 * - Reading order enforcement for screen readers
 * - Return focus to trigger element when closed
 * 
 * Addresses WCAG violations:
 * - KDAP2-2935: Focus should move only within dialog
 * - KDAP2-2936: Focus should follow logical sequence
 * - Reading Order: Ensures meaningful reading order within dialog
 */
const AccessibleDialog: React.FC<AccessibleDialogProps> = ({
    children,
    open,
    returnFocusElement,
    onEnter,
    onExited,
    onClose,
    initialFocus,
    enforceReadingOrder = true, // Enable reading order by default
    readingOrderElements,
    ...dialogProps
}) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const focusTrapRef = useRef<FocusTrap | null>(null);
    const [isMounted, setIsMounted] = useState(false);

    // Initialize focus trap when dialog opens
    useEffect(() => {
        if (open && dialogRef.current && isMounted) {
            const focusTrapOptions: FocusTrapOptions = {
                container: dialogRef.current,
                returnFocusElement,
                initialFocus,
                enforceReadingOrder,
                readingOrderElements,
                onActivate: () => {
                    onEnter?.();
                },
                onDeactivate: () => {
                    onExited?.();
                }
            };

            focusTrapRef.current = new FocusTrap(focusTrapOptions);
            focusTrapRef.current.activate();
        }

        return () => {
            if (focusTrapRef.current) {
                focusTrapRef.current.destroy();
                focusTrapRef.current = null;
            }
        };
    }, [open, isMounted, returnFocusElement, initialFocus, enforceReadingOrder, readingOrderElements, onEnter, onExited]);

    // Handle dialog close
    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        // Deactivate focus trap before closing
        if (focusTrapRef.current) {
            focusTrapRef.current.deactivate();
        }

        onClose?.(event, reason);
    };

    // Mark component as mounted
    useEffect(() => {
        setIsMounted(true);
    }, []);

    return (
        <Dialog
            {...dialogProps}
            open={open}
            onClose={handleClose}
            ref={dialogRef}
            // Ensure proper ARIA attributes
            role="dialog"
            aria-modal="true"
            // Disable backdrop click for better keyboard accessibility
            disableBackdropClick={false}
            // Handle escape key properly
            disableEscapeKeyDown={false}
        >
            {children}
        </Dialog>
    );
};

export default AccessibleDialog;
